import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Icon, Autocomplete, Text } from '@pinpoint/design-kit';

function ClientsSelection({
  loading,
  clients,
  tenant: currentTenant,
  onSelectClient,
  multiSelect,
  defaultText,
  valueIndices,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const currentClient = clients.find(({ tenant }) => tenant === currentTenant);
  const sortedClients = useMemo(
    () =>
      clients.sort((a, b) => {
        // NOTE: Sort requires a number be returned not a boolean. code taken directly from MDN docs
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
        const nameA = a.name.toLowerCase(); // ignore upper and lowercase
        const nameB = b.name.toLowerCase(); // ignore upper and lowercase
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }

        // names must be equal
        return 0;
      }),
    [clients]
  );
  const menuItems = useMemo(() => sortedClients.map(({ name }) => name), [sortedClients]);

  const setBg = useCallback(() => {
    if (isOpen || isHovered) return 'shadeF2';
    return 'white';
  }, [isOpen, isHovered]);
  const setTextColor = useCallback(() => {
    if (isOpen || isHovered) return 'shade66';
    return 'shade99';
  }, [isOpen, isHovered]);
  const bgColor = setBg();
  const fontColor = setTextColor();

  const handleClick = useCallback(() => setIsOpen(!isOpen), [isOpen]);
  const setHovered = useCallback(() => setIsHovered(true), []);
  const setNotHovered = useCallback(() => setIsHovered(false), []);
  const handleSelection = useCallback(
    (index) =>
      multiSelect ? onSelectClient(index.map((i) => sortedClients[i])) : onSelectClient(sortedClients[index]),
    [onSelectClient, sortedClients, multiSelect]
  );

  if (clients?.length === 0) {
    return <Icon color="shade66" m={{ l: '.625rem' }} name="Loading4" viewBox="0 0 18 4" size=".625rem" />;
  }

  if (clients?.length === 1) {
    return (
      <Text m={{ x: '0.5rem' }} textWeight="500" textColor="shade66">
        {clients[0].name}
      </Text>
    );
  }

  return (
    <Autocomplete
      w="20rem"
      h="1.875rem"
      isOpen={isOpen}
      isLoading={loading}
      value={currentClient ? currentClient.name : defaultText}
      valueIndices={valueIndices}
      onClick={handleClick}
      onMouseEnter={setHovered}
      onMouseLeave={setNotHovered}
      isHovered={isHovered}
      multiSelect={multiSelect}
      menu={menuItems}
      onSelection={handleSelection}
      openSuffix={<Icon color="shade66" name="UpArrow" size=".625rem" />}
      closeSuffix={<Icon color="shade66" name="DownArrow" size=".625rem" />}
      data-test="clients-dropdown"
      DropdownButtonOptions={{
        h: '1.875rem',
        w: '2rem',
        bg: bgColor,
        focusBg: bgColor,
        hoverBg: bgColor,
        rounded: { tr: 'md', br: 'md' },
        border: { y: '1px solid', r: '1px solid', l: '0' },
        borderColor: 'shade66',
        align: 'center',
        justify: 'center',
        d: 'flex',
        p: '0',
        transition: 'all 0.2s ease-in-out',
        'data-test': 'clients-dropdown-btn',
      }}
      inputStyle={{
        h: 'calc(1.875rem-1px)',
        textColor: fontColor,
        textWeight: '500',
        bg: bgColor,
        focusBg: bgColor,
        hoverBg: bgColor,
        style: {
          transition: 'all 0.2s ease-in-out',
          font: 'inherit',
        },
        'data-test': 'clients-dropdown-input',
      }}
      DivOptions={{
        rounded: isOpen ? { tl: 'md', bl: '0', br: '0', tr: '0' } : { l: 'md', r: '0' },
        border: isOpen ? { x: '1px solid', t: '1px solid', b: '0' } : '1px solid',
        borderColor: 'shade66',
        bg: bgColor,
        hoverBg: bgColor,
        style: {
          transition: 'all 0.2s ease-in-out',
        },
      }}
      menuItemOptions={{
        p: { y: '.25rem', x: '.75rem' },
        textWeight: '500',
        bg: 'shadeF2',
        hoverBg: 'white',
        'data-test': 'clients-dropdown-menu-item',
      }}
      menuOptions={{
        border: { b: '1px solid', x: '1px solid', t: '0' },
        rounded: { bl: 'md', br: 'md', tr: '0', tl: '0' },
        pos: 'absolute',
        shadow: 4,
        minW: '18rem',
        maxW: '18rem',
        style: {
          transition: 'all 0.2s ease-in-out',
        },
        maxH: '250px',
        overflow: 'auto',
        textColor: fontColor,
        m: { t: '-2px' },
      }}
    />
  );
}

ClientsSelection.propTypes = {
  loading: PropTypes.bool.isRequired,
  tenant: PropTypes.string.isRequired,
  clients: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })).isRequired,
  onSelectClient: PropTypes.func.isRequired,
  dropDownStyle: PropTypes.shape({}),
  multiSelect: PropTypes.bool,
  valueIndices: PropTypes.arrayOf(PropTypes.number),
  defaultText: PropTypes.string,
};

ClientsSelection.defaultProps = {
  dropDownStyle: {
    w: '100%',
  },
  multiSelect: false,
  defaultText: 'Please wait...',
  valueIndices: undefined,
};

export default ClientsSelection;
